import React, { useCallback, useId, useRef } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { AnimatePresence, motion } from 'framer-motion';
import { LuX } from 'react-icons/lu';
import useDialogFocus from '../../hooks/useDialogFocus';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import IconButton from './IconButton';

const DrawerPanel = ({ onClose, title, eyebrow, closeLabel, footer, lang, children }) => {
  const panelRef = useRef(null);
  const closeRef = useRef(null);
  const titleId = useId();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const handleClose = useCallback(() => onCloseRef.current(), []);
  useDialogFocus(panelRef, closeRef, handleClose);
  useBodyScrollLock();

  return (
    <motion.div
      lang={lang}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="tw-fixed tw-inset-0 tw-z-[1100] tw-flex tw-justify-end tw-font-sans tw-antialiased"
    >
      <div aria-hidden="true" onClick={handleClose} className="tw-absolute tw-inset-0 tw-bg-slate-950/40 tw-backdrop-blur-[2px] dark:tw-bg-black/60" />
      <motion.aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', stiffness: 320, damping: 34 }}
        className="tw-relative tw-flex tw-h-full tw-w-full tw-max-w-[30rem] tw-flex-col tw-overflow-clip tw-border-l tw-border-white/80 tw-bg-white tw-text-slate-800 tw-shadow-2xl focus:tw-outline-none sm:tw-rounded-l-[1.75rem] dark:tw-border-white/10 dark:tw-bg-[#1f232b] dark:tw-text-slate-100"
      >
        <div className="tw-flex tw-shrink-0 tw-items-start tw-gap-3 tw-border-b tw-border-slate-200/80 tw-px-5 tw-py-4 dark:tw-border-white/10">
          <div className="tw-min-w-0 tw-flex-1 tw-space-y-1.5">
            {eyebrow}
            <h2 id={titleId} className="tw-break-words tw-text-lg tw-font-semibold tw-tracking-tight tw-text-slate-900 dark:tw-text-white">{title}</h2>
          </div>
          <IconButton ref={closeRef} label={closeLabel} onClick={handleClose}>
            <LuX aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
          </IconButton>
        </div>
        <div className="tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-overscroll-contain tw-px-5 tw-py-4 glass-scrollbar">{children}</div>
        {footer && (
          <div className="tw-shrink-0 tw-border-t tw-border-slate-200/80 tw-bg-slate-50/80 tw-px-5 tw-py-3.5 dark:tw-border-white/10 dark:tw-bg-white/[0.02]">
            {footer}
          </div>
        )}
      </motion.aside>
    </motion.div>
  );
};

/**
 * Side panel that slides in from the right (record details next to a list).
 * Same focus handling as Modal: Escape closes the top-most dialog, the page
 * doesn't scroll behind it; dialogs opened from it stack above.
 */
const Drawer = ({ isOpen, ...panelProps }) =>
  ReactDOM.createPortal(
    <AnimatePresence>{isOpen && <DrawerPanel key="drawer" {...panelProps} />}</AnimatePresence>,
    document.body
  );

Drawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.node.isRequired,
  eyebrow: PropTypes.node,
  closeLabel: PropTypes.string.isRequired,
  footer: PropTypes.node,
  lang: PropTypes.string,
  children: PropTypes.node,
};

export default Drawer;
